import { useEffect, useRef } from 'react';
import styled from 'styled-components';

import {
  useActivityCenterButtonRendered,
  useActivityCenterTotal,
  useIsActivityCenterVisible,
  useSetActivityCenterVisible,
} from '@xing-com/hub';
import type { LinkProps } from '@xing-com/link';
import { Link } from '@xing-com/link';
import { spaceS, xdlColorText } from '@xing-com/tokens';

import { SuperEllipseWithTopBadge } from './super-ellipse-with-top-badge';

type ActivityCenterButtonProps = {
  title: string;
  href?: string;
  loginLink?: string;
};

export const ActivityCenterLoginButton: React.FC<ActivityCenterButtonProps> = ({
  title,
  href,
  loginLink,
}) => {
  useActivityCenterButtonRendered();

  const total = useActivityCenterTotal();
  const setActivityCenterVisible = useSetActivityCenterVisible();
  const isActivityCenterVisible = useIsActivityCenterVisible();

  const justClosedRef = useRef<boolean>(false);
  const navigateTo = total <= 0 && loginLink ? loginLink : href;

  useEffect(() => {
    if (isActivityCenterVisible) return;

    justClosedRef.current = true;

    const timerId = setTimeout(() => {
      justClosedRef.current = false;
    }, 100);

    return () => {
      clearTimeout(timerId);
      justClosedRef.current = false;
    };
  }, [isActivityCenterVisible]);

  const onClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    event.preventDefault();

    if (!justClosedRef.current) {
      setActivityCenterVisible(!isActivityCenterVisible);
    }
  };

  return (
    <StyledLink
      id="activity-center-button"
      data-testid="frame-vnav-activity-center-login-button"
      to={navigateTo}
      aria-label={title}
      onClick={onClick}
    >
      <SuperEllipseWithTopBadge count={total} />
      {title}
    </StyledLink>
  );
};

const StyledLink = styled(Link)<LinkProps>`
  color: ${xdlColorText};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${spaceS};
  &:hover,
  &:focus {
    text-decoration: none;
  }

  position: relative;
`;
